import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchLowStockReport,
  exportLowStockReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchInventoryCategories, fetchAllInventorySubcategories } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { AlertTriangle, Package, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import FormField from 'components/common/FormField';
import CategorySubcategorySelect from 'components/common/CategorySubcategorySelect';
import { formatDateTime } from 'utlis/formateDateTime';

const LowStockReportPage = () => {
  const dispatch = useDispatch();

  const {
    lowStockReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = lowStockReport?.data?.summary || {};
  const results = lowStockReport?.data?.results || [];
  const pagination = lowStockReport?.pagination || {};

  // Get categories and subcategories from Redux state
  const categories = useSelector((state) => state.common?.inventoryCategories.items || []);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'all',
    category: 'all',
    sub_category: 'all',
    current_quantity_gte: '',
    current_quantity_lte: '',
    shortfall_gte: '',
    shortfall_lte: '',
    buffer_threshold_gte: '',
    buffer_threshold_lte: '',
  });

  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);


  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);


  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'low_stock',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.current_quantity_gte && { current_quantity__gte: advancedFilters.current_quantity_gte }),
      ...(advancedFilters.current_quantity_lte && { current_quantity__lte: advancedFilters.current_quantity_lte }),
      ...(advancedFilters.shortfall_gte && { shortfall__gte: advancedFilters.shortfall_gte }),
      ...(advancedFilters.shortfall_lte && { shortfall__lte: advancedFilters.shortfall_lte }),
      ...(advancedFilters.buffer_threshold_gte && { buffer_threshold__gte: advancedFilters.buffer_threshold_gte }),
      ...(advancedFilters.buffer_threshold_lte && { buffer_threshold__lte: advancedFilters.buffer_threshold_lte }),
    };

    dispatch(fetchLowStockReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
    dispatch(fetchAllInventorySubcategories());
  }, [dispatch]);


  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: 'all',
      category: 'all',
      sub_category: 'all',
      current_quantity_gte: '',
      current_quantity_lte: '',
      shortfall_gte: '',
      shortfall_lte: '',
      buffer_threshold_gte: '',
      buffer_threshold_lte: '',
    });
  };

  const columns = [
    { accessorKey: 'Category', header: 'Category' },
    { accessorKey: 'Sub Category', header: 'Sub Category' },
    { accessorKey: 'Current Quantity', header: 'Current Quantity' },
    { accessorKey: 'Buffer Threshold', header: 'Buffer Threshold' },
    {
      accessorKey: 'Shortfall',
      header: 'Shortfall',
      cell: ({ row }) => (
        <Badge variant="destructive">
          {row.original.Shortfall ?? 0}
        </Badge>
      ),
    },
    { accessorKey: 'Last Updated', header: 'Last Updated', cell: ({ row }) => (formatDateTime(row.original?.["Last Updated"]) ?? '-') },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Low Stock Report"
        description="Items currently below buffer threshold requiring immediate attention"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">


              {/* <FormField
                label="Category"
                name="category"
                type="select"
                value={advancedFilters.category}
                onChange={(e) =>
                  handleAdvancedFilterChange('category', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(categories)
                    ? categories.map((category) => ({
                      value: String(category.id),
                      label: category.name,
                    }))
                    : []),
                ]}
              />

              <FormField
                label="Sub Category"
                name="sub_category"
                type="select"
                value={advancedFilters.sub_category}
                onChange={(e) =>
                  handleAdvancedFilterChange('sub_category', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(subcategories)
                    ? subcategories.map((subcategory) => ({
                      value: String(subcategory.id),
                      label: subcategory.name,
                    }))
                    : []),
                ]}
              /> */}


              <CategorySubcategorySelect
                category={advancedFilters.category}
                subCategory={advancedFilters.sub_category}
                onCategoryChange={(value) => {
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    category: value,
                    sub_category: "all",
                  }));
                }}
                onSubCategoryChange={(value) =>
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    sub_category: value,
                  }))
                }
              />


              <div className="space-y-2">
                <Label>Current Quantity Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.current_quantity_gte}
                    onChange={(e) => handleAdvancedFilterChange('current_quantity_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.current_quantity_lte}
                    onChange={(e) => handleAdvancedFilterChange('current_quantity_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Shortfall Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.shortfall_gte}
                    onChange={(e) => handleAdvancedFilterChange('shortfall_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.shortfall_lte}
                    onChange={(e) => handleAdvancedFilterChange('shortfall_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Buffer Threshold Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.buffer_threshold_gte}
                    onChange={(e) => handleAdvancedFilterChange('buffer_threshold_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.buffer_threshold_lte}
                    onChange={(e) => handleAdvancedFilterChange('buffer_threshold_lte', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock Items</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary['Low Stock Items'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Shortfall Quantity</CardTitle>
            <Package className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary['Total Shortfall Quantity'] ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading low stock data...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by category, sub-category..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportLowStockReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'low_stock',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
              ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
              ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
              ...(advancedFilters.current_quantity_gte && { current_quantity__gte: advancedFilters.current_quantity_gte }),
              ...(advancedFilters.current_quantity_lte && { current_quantity__lte: advancedFilters.current_quantity_lte }),
              ...(advancedFilters.shortfall_gte && { shortfall__gte: advancedFilters.shortfall_gte }),
              ...(advancedFilters.shortfall_lte && { shortfall__lte: advancedFilters.shortfall_lte }),
              ...(advancedFilters.buffer_threshold_gte && { buffer_threshold__gte: advancedFilters.buffer_threshold_gte }),
              ...(advancedFilters.buffer_threshold_lte && { buffer_threshold__lte: advancedFilters.buffer_threshold_lte }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default LowStockReportPage;